import { ApiError, apiErrorReason } from '@/lib/api';

// Validación de campos y mensajes del alta de documento (QA F1 #6/#15). La regla del
// código es la misma del API (documentCodeSchema): el usuario ve el error antes de
// enviar, y el API sigue siendo la autoridad. Cero enums crudos (UX-COPY-01).

const CODE_PATTERN = /^[\p{L}\p{N}](?:[\p{L}\p{N}._/-]*[\p{L}\p{N}])?$/u;
const CODE_MAX = 60;
const TITLE_MAX = 200;

/** Error del código (ya recortado por el llamador o no), o null si es válido. */
export function codeError(raw: string): string | null {
  const code = raw.trim();
  if (code === '') {
    return 'Escribe el código del documento';
  }
  if (code.length > CODE_MAX) {
    return `El código admite hasta ${CODE_MAX} caracteres`;
  }
  return CODE_PATTERN.test(code) ? null : 'Usa solo letras, números y los separadores . _ - /';
}

export function titleError(raw: string): string | null {
  const title = raw.trim();
  if (title === '') {
    return 'Escribe el nombre del documento';
  }
  return title.length > TITLE_MAX ? `El nombre admite hasta ${TITLE_MAX} caracteres` : null;
}

const REJECTIONS: Record<string, string> = {
  code_in_use: 'Ese código ya está reservado o en uso. Elige otro.',
  code_retired: 'Ese código fue de un documento obsoleto y no se reutiliza. Elige otro.',
  same_code: 'El código nuevo es igual al actual.',
  not_reserved: 'El documento ya no está reservado. Recarga la ficha para ver su estado actual.',
  invalid_document_type: 'El tipo de documento ya no está disponible. Elige otro.',
  invalid_process_area: 'El proceso ya no está disponible. Elige otro.',
  invalid_owner: 'El dueño de proceso no pertenece a esta empresa.',
  code_in_lms:
    'Este documento ya se envió al LMS con su código. Para usar otro, anula la reserva y registra uno nuevo.',
  not_editable:
    'El documento fue anulado: ya no admite cambios de dueño de proceso. Recarga la ficha.',
  not_linkable:
    'El archivo solo se vincula mientras el documento está reservado o en borrador. Recarga la ficha.',
  lms_document_not_found: 'El LMS ya no muestra ese documento para esta empresa. Busca de nuevo.',
  title_retired:
    'Ese nombre fue de un documento obsoleto de este tipo y no se reutiliza. Elige otro.',
  code_required: 'Este tipo no tiene patrón: escribe el código.',
  sap_module_required: 'Elige el módulo de SAP del registro.',
  sap_module_invalid: 'El módulo de SAP no está en la tabla de 04-GC-P08. Elige otro.',
};
// El mensaje del API es más preciso: nombra al otro documento, las versiones del LMS y del
// documento, da el ejemplo del patrón o el proceso que falta configurar.
const SERVER_MESSAGE = new Set([
  'lms_document_linked',
  'lms_version_mismatch',
  'code_pattern_mismatch',
  'process_type_missing',
  'process_tree_invalid',
  'process_group_selected',
]);

/** Mensaje accionable para un rechazo del alta, la corrección, la anulación o el vínculo. */
export function registrationError(err: unknown): string {
  const reason = apiErrorReason(err);
  if (reason !== undefined && SERVER_MESSAGE.has(reason) && err instanceof ApiError) {
    return err.message;
  }
  if (reason !== undefined && REJECTIONS[reason] !== undefined) {
    return REJECTIONS[reason];
  }
  if (err instanceof ApiError && err.status === 403) {
    return 'No tienes permiso para esta acción. Pide acceso al Administrador de Calidad.';
  }
  return 'No se pudo guardar. Intenta de nuevo.';
}
